import React, { createContext, useContext, ReactNode } from 'react'
import { useAuth, OrgRole } from '../../contexts/AuthContext'

interface OrganizationContextType {
  hasOrganization: boolean
  isOwner: boolean
  isAdmin: boolean
  isMember: boolean
  canInvite: boolean
  canManage: boolean
  canRemoveMembers: boolean
  isAtMemberLimit: boolean
  availableSlots: number
  checkRole: (allowedRoles: OrgRole[]) => boolean
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined)

interface OrganizationProviderProps {
  children: ReactNode
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const { currentOrg, orgRole, canInviteMembers, canManageOrg } = useAuth()

  const hasOrganization = !!currentOrg
  const isOwner = orgRole === 'org:owner'
  const isAdmin = orgRole === 'org:admin'
  const isMember = orgRole === 'org:member'
  
  const canInvite = canInviteMembers && currentOrg ? currentOrg.membersCount < currentOrg.maxMembers : false
  const canManage = canManageOrg
  const canRemoveMembers = isOwner || isAdmin
  
  const isAtMemberLimit = currentOrg ? currentOrg.membersCount >= currentOrg.maxMembers : false
  const availableSlots = currentOrg ? Math.max(0, currentOrg.maxMembers - currentOrg.membersCount) : 0

  const checkRole = (allowedRoles: OrgRole[]): boolean => {
    if (!orgRole) return false
    return allowedRoles.includes(orgRole)
  }

  const value: OrganizationContextType = {
    hasOrganization,
    isOwner,
    isAdmin,
    isMember,
    canInvite,
    canManage,
    canRemoveMembers,
    isAtMemberLimit,
    availableSlots,
    checkRole,
  }

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  )
}

export const useOrganizationContext = () => {
  const context = useContext(OrganizationContext)
  if (context === undefined) {
    throw new Error('useOrganizationContext must be used within an OrganizationProvider')
  }
  return context
}


